import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { OrderState } from "../../enums/order-state.enum";
import { Order } from "../../models/order.model";
import { OrderStatePipe } from "../../pipes/order-state.pipe";
import { ShipmentStateEnum } from "../../enums/shipment-state.enum";
import { ShipmentGroup } from "../../models/shipment-group.interface";
import { BehaviorSubject } from "rxjs";
import { Quotation } from "../../models/quotation.model";
import { QuotationState } from "../../enums/quotation-state.enum";
import { TranslateService } from "@ngx-translate/core";
import { TransporterEnum } from "../../enums/transporter.enum";
import { RolesEnum } from "../../enums/roles.enum";
import { MatSelectChange } from "@angular/material/select";
import { AuthService } from "../../services/auth.service";
import { ShipmentService } from "../../services/shipment.service";
import { ExternalProduction } from "../../models/external-production.model";
import { ExternalProductionState } from "../../enums/external-production-state.enum";
import { SubcontractRequest } from "../../models/subcontract-request.model";
import { SubcontractRequestState } from "../../enums/subcontract-request-state.enum";

@Component({
    selector: 'app-state-indicator',
    templateUrl: './state-indicator.component.html',
    styleUrls: ['./state-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StateIndicatorComponent {
  @Input() order: Order;
  @Input() shipmentGroup: ShipmentGroup;
  @Input() quotation: Quotation;
  @Input() externalProduction: ExternalProduction;
  @Input() subcontractRequest: SubcontractRequest;
  protected readonly OrderState = OrderState;
  protected readonly ShipmentStateEnum = ShipmentStateEnum;
  protected readonly QuotationState = QuotationState;
  protected readonly TransporterEnum = TransporterEnum;
  protected readonly RolesEnum = RolesEnum;
  protected readonly ExternalProductionState = ExternalProductionState;
  protected readonly SubcontractRequestState = SubcontractRequestState;
  public states$ = new BehaviorSubject([{
    index: ShipmentStateEnum.TO_SHIP,
    label: 'shipment.state.toShip'
  }, {
    index: ShipmentStateEnum.PLANNED,
    label: 'shipment.state.planned'
  }, {
    index: ShipmentStateEnum.TO_COLLECT,
    label: 'shipment.state.toCollect'
  }, {
    index: ShipmentStateEnum.IN_TRANSIT,
    label: 'shipment.state.inTransit'
  }, {
    index: ShipmentStateEnum.DELIVERED,
    label: 'shipment.state.delivered'
  }, {
    index: ShipmentStateEnum.CANCELLED,
    label: 'shipment.state.cancelled'
  }]);
  constructor(private orderPipe: OrderStatePipe,
              public authService: AuthService,
              private shipmentService: ShipmentService,
              private translate: TranslateService) {
  }

  /**
   * Generate tooltip content to indicate order state
   * @param order The order
   * @returns the tooltip string
   */
  public getOrderTooltip(order: Order): string {
    if (order.state != OrderState.BAT_REFUSED) {
      return this.orderPipe.transform(order);
    }
    let content = '';
    for (const pr of order.products) {
      if (!pr.batReason) {
        continue;
      }
      content += '\n' + pr.productName + ' : ' + pr.batReason;
    }
    return `${this.orderPipe.transform(order)} : ${content}`;
  }

  /**
   * Generate tooltip content to indicate shipment group state
   * @param shipmentGroup order
   * @returns the tooltip string
   */
  public getShipmentGroupTooltip(shipmentGroup: ShipmentGroup): string {
    return this.states$.value.find(state => shipmentGroup.state === state.index)?.label ?? '';
  }

  /**
   * Generate tooltip content to indicate quotation state
   * @param quotation order
   * @returns the tooltip string
   */
  public getQuotationTooltip(quotation: Quotation): string {
    return this.translate.instant('quotation.state.' + quotation.state);
  }

  /**
   * Update the state of a shipment group
   * @param event the event
   * @param group the group
   */
  public async updateState(event: MatSelectChange, group: ShipmentGroup): Promise<void> {
    await this.shipmentService.updateState(group.shipments, event.value);
  }

  /**
   * Generate tooltip content to indicate external production state
   * @param externalProduction ExternalProduction
   * @returns The tooltip content
   */
  public getExternalProductionTooltip(externalProduction: ExternalProduction): string {
    return this.translate.instant(externalProduction.state);
  }

  /**
   * Generate tooltip content to indicate external production state
   * @param subcontractRequest SubcontractRequest
   * @returns The tooltip content
   */
  public getSubcontractRequestTooltip(subcontractRequest: SubcontractRequest): string {
    const state =  this.translate.instant(subcontractRequest.state);
    const comment = subcontractRequest.comment ? `: ${subcontractRequest.comment}` : '';
    return state + comment;
  }
}
